<script setup lang="ts">
import { useAuthStore } from '@/stores/storeAuth';
import { useFirebaseAppStore } from '@/stores/storeFirebaseApp';
import { useRouter, useRoute } from 'vue-router';
import { computed, ref } from 'vue';
import { resolvePostLoginRoute } from '@/services/authRedirect';

const authStore = useAuthStore();
const firebaseAppStore = useFirebaseAppStore();
const router = useRouter();
const route = useRoute();

// The nav links are d-none below `md` (see .lg-nav-gap below) with no other
// way to reach them on a narrow window -- this drawer is that fallback,
// mirroring the exact same isDashboard/isAdmin branches as the desktop nav.
const mobileNavOpen = ref(false);

const isDashboard = computed(() => route.matched.some(record => record.meta.requiresAuth) === true);
// Admin is a platform-configuration role, not a user running their own
// tests -- they don't need sessions, partner integrations, or other
// user-facing actions in their nav, only the maintenance views
// (User Management, Org Hierarchy, Waiting List).
const isAdmin = computed(() => firebaseAppStore.currentUser?.role?.toLowerCase() === 'admin');

// Sign out *first*, then navigate.
//
// Navigating while still authenticated made the router's "redirect a signed-in
// user away from /login" guard fire, and for an admin that guard resolves to
// resolvePostLoginRoute -> /architecture. So logging out as an admin raced
// signOut() against a navigation to an admin-only page, and landed on the
// architecture reference with nobody signed in.
async function logout() {
	await authStore.logout();
	router.replace('/login');
}

function handleAuthLink(path: string) {
	if (!authStore.user.uid) {
		router.push('/login');
	} else {
		router.push(path);
	}
}

function toggleSupport() {
	handleAuthLink('/support');
}

// Same destination as signing in resolves to. This branched identically by
// hand and still sent admins to /admin/users, so clicking "Dashboard" landed
// them somewhere other than where login does.
function handleResultsClick() {
	handleAuthLink(resolvePostLoginRoute(firebaseAppStore));
}
</script>
<template>
	<v-app-bar app color="white" elevation="0" height="64" class="lg-header-border px-md-4">
		<v-app-bar-nav-icon class="d-flex d-md-none" @click="mobileNavOpen = !mobileNavOpen" />

		<v-app-bar-title>
			<router-link to="/" class="d-flex align-center text-decoration-none">
				<span class="text-h6 font-weight-black text-secondary lg-wordmark">LEGIZMO</span>
				<div class="ms-2 bg-primary px-1 text-caption font-weight-bold text-secondary">PRO</div>
			</router-link>
		</v-app-bar-title>

		<div class="d-none d-md-flex align-center lg-nav-gap">
			<!-- Dashboard Mode Menu -->
			<template v-if="isDashboard">
				<!-- Admin is configuration-only: no personal Dashboard/sessions,
				     no Partner Integrations -- just the maintenance views. -->
				<template v-if="isAdmin">
					<v-btn variant="text" to="/architecture" class="lg-nav-link">Architecture</v-btn>
					<v-btn variant="text" to="/admin/org" class="lg-nav-link">Org Hierarchy</v-btn>
					<v-btn variant="text" to="/admin/users" class="lg-nav-link">User Management</v-btn>
					<v-btn variant="text" to="/admin/devices" class="lg-nav-link">Aero Sensors</v-btn>
					<v-btn variant="text" to="/admin/firmware" class="lg-nav-link">Firmware</v-btn>
					<v-btn variant="text" to="/admin/waiting-list" class="lg-nav-link">Waiting List</v-btn>
				</template>
				<template v-else>
					<!-- Single "Dashboard" entry point -- handleResultsClick already
					     branches by role (manager -> /my-team, athlete -> /my-profile),
					     so a separate "My Team" link here was just a duplicate of the
					     same destination for managers, not a distinct page. -->
					<v-btn variant="text" @click="handleResultsClick" class="lg-nav-link">Dashboard</v-btn>
					<v-btn variant="text" to="/analytics" class="lg-nav-link">Analytics</v-btn>
					<v-btn variant="text" to="/integrations" class="lg-nav-link">Partner Integrations</v-btn>
				</template>
				<v-btn variant="text" to="/" class="lg-nav-link">Return to Home</v-btn>
			</template>
			<!-- Public Website Mode Menu -->
			<template v-else>
				<v-btn variant="text" to="/" class="lg-nav-link">Product</v-btn>
				<v-btn variant="text" to="/seminars" class="lg-nav-link">Seminars</v-btn>
				<v-btn variant="text" to="/technology" class="lg-nav-link">Technology</v-btn>
				<!-- Tools (Virtual Elevation) nav link intentionally hidden for now --
				     public/no-login access is disabled (route now requiresAuth) until
				     this is ready to be public. Re-add here + the mobile drawer below
				     to re-enable. -->
				<v-btn variant="text" @click="handleResultsClick" class="lg-nav-link">{{ authStore.user.uid ? 'Dashboard' : 'Results' }}</v-btn>
				<v-btn variant="text" to="/about" class="lg-nav-link">About</v-btn>
			</template>
		</div>

		<v-spacer />

		<v-menu offset-y v-if="authStore.user.uid">
			<template #activator="{ props }">
				<v-btn v-bind="props" variant="outlined" color="secondary" class="font-weight-bold border-opacity-100">
					<v-avatar v-if="firebaseAppStore.currentUser?.photoURL" size="20" class="me-2">
						<v-img :src="firebaseAppStore.currentUser.photoURL" alt="" />
					</v-avatar>
					<v-icon v-else left size="small" class="me-2">mdi-account</v-icon>
					<span class="d-none d-sm-inline">{{ authStore.user?.email?.split('@')[0] }}</span>
				</v-btn>
			</template>

			<v-list density="compact" elevation="4" rounded="0" class="border pb-2">
				<v-list-item class="px-4 py-3 bg-grey-lighten-4 mb-2">
					<div class="d-flex flex-column">
						<div class="text-subtitle-2 font-weight-black text-secondary">{{ firebaseAppStore.currentUser?.fullname || authStore.user?.fullname || 'User' }}</div>
						<div class="text-caption text-grey-darken-1 mb-1">{{ authStore.user?.email }}</div>
						<div>
							<v-chip size="x-small" color="primary" class="font-weight-bold uppercase mt-1" label>
								{{ firebaseAppStore.currentUser?.role || 'User' }}
							</v-chip>
						</div>
					</div>
				</v-list-item>
				
				<v-divider class="mb-2"></v-divider>

				<v-list-item v-if="authStore.user.uid" to="/my-profile">
					<v-list-item-title class="text-caption font-weight-bold uppercase">
						<v-icon size="small" class="me-2">mdi-account-circle</v-icon>
						My Profile
					</v-list-item-title>
				</v-list-item>

				<v-list-item v-if="authStore.user.uid && (firebaseAppStore.currentUser?.role?.toLowerCase() === 'admin' || firebaseAppStore.currentUser?.role?.toLowerCase() === 'coach')" to="/manage-events">
					<v-list-item-title class="text-caption font-weight-bold uppercase">
						<v-icon size="small" class="me-2">mdi-calendar-edit</v-icon>
						Manage Events
					</v-list-item-title>
				</v-list-item>

				<v-list-item v-if="authStore.user.uid" to="/coaching">
					<v-list-item-title class="text-caption font-weight-bold uppercase">
						<v-icon size="small" class="me-2">mdi-account-supervisor</v-icon>
						Coaching
					</v-list-item-title>
				</v-list-item>

				<v-list-item v-if="authStore.user.uid && isDashboard && !isAdmin" to="/integrations">
					<v-list-item-title class="text-caption font-weight-bold uppercase">
						<v-icon size="small" class="me-2">mdi-sync</v-icon>
						Partner Integrations
					</v-list-item-title>
				</v-list-item>

				<v-list-item @click="logout">
					<v-list-item-title class="text-caption font-weight-bold uppercase">
						<v-icon size="small" class="me-2">mdi-logout</v-icon>
						Log Out
					</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<div v-else class="d-flex gap-2">
			<v-btn to="/login" variant="text" color="secondary" class="font-weight-bold">
				Log In
			</v-btn>
			<v-btn to="/register" color="secondary" variant="flat" class="font-weight-bold border">
				Request Access
			</v-btn>
		</div>
	</v-app-bar>

	<!-- Mobile nav fallback (< md): same links as the desktop .lg-nav-gap div,
	     as a right-side drawer instead of a horizontal bar. -->
	<v-navigation-drawer v-model="mobileNavOpen" location="right" temporary width="280" class="d-md-none">
		<v-list density="comfortable" nav>
			<template v-if="isDashboard">
				<template v-if="isAdmin">
					<v-list-item to="/architecture" title="Architecture" @click="mobileNavOpen = false" />
					<v-list-item to="/admin/org" title="Org Hierarchy" @click="mobileNavOpen = false" />
					<v-list-item to="/admin/users" title="User Management" @click="mobileNavOpen = false" />
					<v-list-item to="/admin/devices" title="Aero Sensors" @click="mobileNavOpen = false" />
					<v-list-item to="/admin/firmware" title="Firmware" @click="mobileNavOpen = false" />
					<v-list-item to="/admin/waiting-list" title="Waiting List" @click="mobileNavOpen = false" />
				</template>
				<template v-else>
					<v-list-item title="Dashboard" @click="handleResultsClick(); mobileNavOpen = false" />
					<v-list-item to="/analytics" title="Analytics" @click="mobileNavOpen = false" />
					<v-list-item to="/integrations" title="Partner Integrations" @click="mobileNavOpen = false" />
				</template>
				<v-list-item to="/" title="Return to Home" @click="mobileNavOpen = false" />
			</template>
			<template v-else>
				<v-list-item to="/" title="Product" @click="mobileNavOpen = false" />
				<v-list-item to="/seminars" title="Seminars" @click="mobileNavOpen = false" />
				<v-list-item to="/technology" title="Technology" @click="mobileNavOpen = false" />
				<v-list-item :title="authStore.user.uid ? 'Dashboard' : 'Results'" @click="handleResultsClick(); mobileNavOpen = false" />
				<v-list-item to="/about" title="About" @click="mobileNavOpen = false" />
			</template>
		</v-list>
	</v-navigation-drawer>
</template>

<style scoped>
/*
 * Wrapped in the `overrides` layer (declared first in app.css) so these
 * !important rules actually beat Vuetify's own !important utility classes —
 * in particular .text-none and .font-weight-medium, which the global VBtn
 * defaults (src/plugins/vuetify.ts) inject onto every button, including
 * these nav links. Without this layer the nav rendered in mixed case at
 * font-weight 500, not the intended uppercase/700 — see the comment in
 * app.css for why plain unlayered !important cannot win here.
 */
@layer overrides {
	.lg-header-border {
		border-bottom: 2px solid #0A0A0A !important;
	}

	.lg-nav-link {
		font-size: 0.75rem !important;
		font-weight: 700 !important;
		letter-spacing: 0.05em !important;
		text-transform: uppercase !important;
		color: #0A0A0A !important;
		opacity: 0.7;
		transition: opacity 0.2s ease;
	}

	.lg-nav-link:hover {
		opacity: 1;
	}

	.lg-wordmark {
		letter-spacing: -0.05em !important;
	}

	.lg-nav-gap {
		gap: 1.5rem;
	}
}
</style>
